import { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Stepper, Step, StepLabel, FormControl,
  Select, MenuItem, InputLabel, TextField, CircularProgress, Box, Typography, Chip,
} from '@mui/material';
import { Widget, ChartOptions, TimeRange, DeviceListItem, DeviceKeyItem } from '../../types/dashboard';
import { getDeviceList, getDeviceKeys } from '../../services/dashboardService';
import { generateUUID } from '../../utils/uuid';
import ChartTemplatePicker, { CHART_TEMPLATES, ChartTemplate } from './ChartTemplatePicker';

const PALETTES: { name: string; colors: string[] }[] = [
  { name: 'palette1',  colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'] },
  { name: 'palette2',  colors: ['#3F51B5', '#03A9F4', '#4CAF50', '#F9CE1D', '#FF9800'] },
  { name: 'palette3',  colors: ['#33B2DF', '#546E7A', '#D4526E', '#13D8AA', '#A5978B'] },
  { name: 'palette4',  colors: ['#4ECDC4', '#C7F464', '#FF6B6B', '#88D8B0', '#A8E6CF'] },
  { name: 'palette5',  colors: ['#FD6A6A', '#4CA3DD', '#A1DE93', '#FDDD8C', '#EB84B5'] },
  { name: 'palette6',  colors: ['#2E294E', '#1B998B', '#E84855', '#FF9F1C', '#FFBF69'] },
  { name: 'palette7',  colors: ['#00CC99', '#00CCFF', '#0066CC', '#3300FF', '#CC33FF'] },
  { name: 'palette8',  colors: ['#D7263D', '#1B1F3B', '#02A9EA', '#85BB65', '#F46036'] },
  { name: 'palette9',  colors: ['#F7B731', '#FC5C65', '#26de81', '#45AAF2', '#A55EEA'] },
  { name: 'palette10', colors: ['#0652DD', '#1289A7', '#C4E538', '#FDA7DF', '#ED4C67'] },
];

const TIME_RANGES: { label: string; value: TimeRange }[] = [
  { label: '15 Dakika', value: '15m' },
  { label: '1 Saat', value: '1h' },
  { label: '6 Saat', value: '6h' },
  { label: '24 Saat', value: '24h' },
  { label: '2 Gün', value: '2d' },
];

const STEPS = ['Sensör Seç', 'Key Seç', 'Grafik Şablonu', 'Başlık & Aralık'];

interface Props {
  existingWidget?: Widget;
  onSave: (widget: Widget) => void;
  onClose: () => void;
}

const WidgetModal = ({ existingWidget, onSave, onClose }: Props) => {
  const [step, setStep] = useState(0);
  const [devices, setDevices] = useState<DeviceListItem[]>([]);
  const [keyItems, setKeyItems] = useState<DeviceKeyItem[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);

  const [deviceId, setDeviceId] = useState<number | null>(existingWidget?.deviceId ?? null);
  const [deviceName, setDeviceName] = useState(existingWidget?.deviceName ?? '');
  const [selectedKeyItems, setSelectedKeyItems] = useState<DeviceKeyItem[]>([]);

  const getInitialTemplateId = () => {
    if (!existingWidget) return CHART_TEMPLATES[0].id;
    const o = existingWidget.chartOptions;
    if (existingWidget.type === 'bar') {
      if (o?.horizontal && o?.timeGrouped) return 'grouped-bar';
      if (o?.horizontal) return 'horizontal-bar';
      return 'column-bar';
    }
    if (existingWidget.type === 'radialBar' && o?.semicircle) return 'semi-gauge';
    return CHART_TEMPLATES.find(t => t.type === existingWidget.type)?.id ?? CHART_TEMPLATES[0].id;
  };
  const [selectedTemplateId, setSelectedTemplateId] = useState(getInitialTemplateId);
  const [chartOptions, setChartOptions] = useState<ChartOptions>(() => {
    const base = existingWidget?.chartOptions ?? { ...CHART_TEMPLATES[0].chartOptions };
    if (!base.paletteColors) {
      const paletteName = base.palette ?? 'palette1';
      base.paletteColors = PALETTES.find(p => p.name === paletteName)?.colors;
    }
    return base;
  });
  const [title, setTitle] = useState(existingWidget?.title ?? '');
  const [timeRange, setTimeRange] = useState<TimeRange>(existingWidget?.timeRange ?? '1h');

  const selectedTemplate = CHART_TEMPLATES.find(t => t.id === selectedTemplateId) ?? CHART_TEMPLATES[0];

  useEffect(() => {
    setLoadingDevices(true);
    getDeviceList().then(setDevices).catch(() => {}).finally(() => setLoadingDevices(false));
  }, []);

  const existingKeyIds = existingWidget?.keyIds ?? [];

  useEffect(() => {
    if (deviceId == null) return;
    setLoadingKeys(true);
    getDeviceKeys(deviceId).then(items => {
      setKeyItems(items);
      if (existingKeyIds.length > 0) {
        setSelectedKeyItems(items.filter(k => existingKeyIds.includes(k.id)));
      }
    }).catch(() => {}).finally(() => setLoadingKeys(false));
  }, [deviceId]);

  const handleDeviceChange = (id: number) => {
    const device = devices.find(d => d.id === id);
    setDeviceId(id);
    setDeviceName(device?.name ?? '');
    setSelectedKeyItems([]);
    setKeyItems([]);
  };

  const toggleKey = (item: DeviceKeyItem) =>
    setSelectedKeyItems(prev =>
      prev.some(k => k.id === item.id) ? prev.filter(k => k.id !== item.id) : [...prev, item]
    );

  const handleTemplateSelect = (template: ChartTemplate) => {
    setSelectedTemplateId(template.id);
    const paletteName = template.chartOptions.palette ?? 'palette1';
    const paletteColors = PALETTES.find(p => p.name === paletteName)?.colors;
    setChartOptions({ ...template.chartOptions, paletteColors });
  };

  const canNext = () => {
    if (step === 0) return deviceId != null;
    if (step === 1) return selectedKeyItems.length > 0;
    return true;
  };

  const handleSave = () => {
    onSave({
      id: existingWidget?.id ?? generateUUID(),
      deviceId,
      deviceName,
      type: selectedTemplate.type,
      title: title || deviceName,
      keys: selectedKeyItems.map(k => k.name),
      keyIds: selectedKeyItems.map(k => k.id),
      timeRange,
      chartOptions,
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
        {existingWidget ? 'Grafik Düzenle' : 'Yeni Grafik Ekle'}
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Stepper activeStep={step} sx={{ mb: 3 }} alternativeLabel>
          {STEPS.map(label => (
            <Step key={label}><StepLabel>{label}</StepLabel></Step>
          ))}
        </Stepper>

        {/* Adım 1 — Sensör */}
        {step === 0 && (
          <FormControl fullWidth>
            <InputLabel>Sensör / Cihaz</InputLabel>
            {loadingDevices ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
            ) : (
              <Select value={deviceId ?? ''} label="Sensör / Cihaz" onChange={e => handleDeviceChange(Number(e.target.value))}>
                {devices.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </Select>
            )}
          </FormControl>
        )}

        {/* Adım 2 — Key */}
        {step === 1 && (
          <Box>
            {loadingKeys ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>
            ) : keyItems.length === 0 ? (
              <Typography color="text.secondary">Bu cihaz için key bulunamadı.</Typography>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textAlign: 'center' }}>
                  Grafikte gösterilecek parametreleri seçin
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {keyItems.map(item => {
                    const isSelected = selectedKeyItems.some(k => k.id === item.id);
                    return (
                      <Chip
                        key={item.id}
                        label={item.name}
                        onClick={() => toggleKey(item)}
                        color={isSelected ? 'primary' : 'default'}
                        variant={isSelected ? 'filled' : 'outlined'}
                        sx={{ fontSize: 14, px: 0.5 }}
                      />
                    );
                  })}
                </Box>
                {selectedKeyItems.length > 0 && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block', textAlign: 'center' }}>
                    {selectedKeyItems.length} parametre seçildi
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Adım 3 — Şablon seçici */}
        {step === 2 && (
          <ChartTemplatePicker selected={selectedTemplateId} onSelect={handleTemplateSelect} />
        )}

        {/* Adım 4 — Başlık & Aralık */}
        {step === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              label="Grafik Başlığı"
              value={title}
              onChange={e => setTitle(e.target.value)}
              fullWidth
              placeholder={deviceName}
            />
            <FormControl fullWidth>
              <InputLabel>Zaman Aralığı</InputLabel>
              <Select value={timeRange} label="Zaman Aralığı" onChange={e => setTimeRange(e.target.value as TimeRange)}>
                {TIME_RANGES.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
              </Select>
            </FormControl>
            <Box>
              <Typography variant="body2" fontWeight={600} gutterBottom>Renk Teması</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
                {PALETTES.map(p => {
                  const selected = (chartOptions.palette ?? selectedTemplate.chartOptions.palette) === p.name;
                  return (
                    <Box
                      key={p.name}
                      onClick={() => setChartOptions(prev => ({ ...prev, palette: p.name, paletteColors: p.colors }))}
                      sx={{
                        cursor: 'pointer', borderRadius: 1.5, p: 0.75,
                        border: 2, borderColor: selected ? 'primary.main' : 'divider',
                        backgroundColor: selected ? 'primary.50' : 'background.paper',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.light', backgroundColor: 'action.hover' },
                      }}
                    >
                      <Box sx={{ display: 'flex', height: 18, borderRadius: 1, overflow: 'hidden' }}>
                        {p.colors.map(c => (
                          <Box key={c} sx={{ flex: 1, backgroundColor: c }} />
                        ))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid', borderColor: 'divider', px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">İptal</Button>
        {step > 0 && <Button onClick={() => setStep(s => s - 1)} color="inherit">Geri</Button>}
        {step < STEPS.length - 1 ? (
          <Button variant="contained" onClick={() => setStep(s => s + 1)} disabled={!canNext()}>İleri</Button>
        ) : (
          <Button variant="contained" onClick={handleSave}>{existingWidget ? 'Kaydet' : 'Ekle'}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WidgetModal;
